/**
 * ドラッグ中の重ね描き（FR-007 / FR-011）。
 *
 * 盤面の論理座標（360 × 480）を `viewBox` に取る SVG レイヤー 1 枚に、
 *
 * - ドロップ候補マスのハイライト（FR-007）
 * - ドロップ予測の枠: 赤 = 分解される / 白 = 押しのけられるが維持される（FR-011）
 *
 * を描く。SVG にするのは、凹角の円弧（`A` コマンド）と穴の縁を素直に表現でき、枠だけを
 * 1 つの層にまとめて描画順を守れるため（`04-architecture.md`「ドロップ予測の枠」）。
 * パスは合成時の解像度でラスタライズされるため、どの拡大倍率でも輪郭が立つ。
 *
 * ここは view であり判断を持たない。どのマスを候補にするか、どのグループがどちらの枠かは
 * controller が `core` に問い合わせて渡す。
 */

import { BOARD_H, BOARD_W } from '../core/constants';
import { col, row } from '../core/grid';
import type { CellIndex } from '../core/types';
import {
  DRAG_LIFT_PX,
  PREVIEW_OFFSET,
  SHADOW_BLUR_STD,
  SHADOW_PANEL_Z,
  SHADOW_PANELS,
} from './constants';
import { outlinePath } from './outline';
import { pieceSize } from './piece-view';

/** 枠で囲む footprint（セル集合）を結末別に分けたもの。 */
export interface OutlineFootprints {
  /** このドロップで分解されるグループの footprint。赤枠になる。 */
  readonly decomposed: readonly (readonly CellIndex[])[];
  /** 押しのけられるが維持されるグループの footprint。白枠になる。 */
  readonly displaced: readonly (readonly CellIndex[])[];
}

/** ドラッグレイヤーのハンドル。 */
export interface DragLayer {
  /** ドロップ候補マスをハイライトする。空配列で消える。 */
  readonly setCandidate: (cells: readonly CellIndex[]) => void;
  /** ドロップ予測の枠を描く。両方空で消える。 */
  readonly setOutlines: (footprints: OutlineFootprints) => void;
  /**
   * グループ影の形を確定する（TK-042、ドラッグ開始時）。影はこのレイヤー（ドラッグ中の
   * ピースより奥、静止ピース・地より手前）に描かれるため、**ドラッグ中のグループ自身には
   * 落ちない**。
   */
  readonly beginShadow: (cells: readonly CellIndex[]) => void;
  /** グループ影をドラッグ変位 (dx, dy) に追従させる。 */
  readonly moveShadow: (dx: number, dy: number) => void;
  /** すべて消す。 */
  readonly clear: () => void;
}

const SVG_NS = 'http://www.w3.org/2000/svg';

/**
 * `host`（盤面ラッパ）に SVG レイヤーを差し込む。
 *
 * 重なり順は CSS の z-index で制御する: 静止ピースの上、ドラッグ中のグループの下
 * （`board.css`）。`pointer-events: none` でヒットテストから外れるため、ドラッグの
 * 入力を遮らない。
 */
export function createDragLayer(host: HTMLElement, C: number, R: number): DragLayer {
  const size = pieceSize(C, R);
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('class', 'drag-layer');
  svg.setAttribute('viewBox', `0 0 ${BOARD_W} ${BOARD_H}`);
  svg.setAttribute('aria-hidden', 'true');

  // 影 < 候補マス < 枠 の順に奥から描くため、グループ要素で層を分ける。
  // 影のぼかしは SVG フィルタで行う（値は view/constants.ts の SHADOW_BLUR_STD）。
  const defs = document.createElementNS(SVG_NS, 'defs');
  const filter = document.createElementNS(SVG_NS, 'filter');
  filter.setAttribute('id', 'drag-shadow-blur');
  // ぼかしの裾がフィルタ領域で切れないよう広めに取る。
  filter.setAttribute('x', '-20%');
  filter.setAttribute('y', '-20%');
  filter.setAttribute('width', '140%');
  filter.setAttribute('height', '140%');
  const blur = document.createElementNS(SVG_NS, 'feGaussianBlur');
  blur.setAttribute('stdDeviation', String(SHADOW_BLUR_STD));
  filter.append(blur);
  defs.append(filter);

  const shadowGroup = document.createElementNS(SVG_NS, 'g');
  shadowGroup.setAttribute('class', 'drag-shadow');
  shadowGroup.setAttribute('filter', 'url(#drag-shadow-blur)');
  const candidateGroup = document.createElementNS(SVG_NS, 'g');
  const outlineGroup = document.createElementNS(SVG_NS, 'g');
  svg.append(defs, shadowGroup, candidateGroup, outlineGroup);
  host.append(svg);

  // グループ影（TK-042）。形（グループ輪郭）はドラッグ開始時に 1 度だけ作り、
  // 追従は 3 パネルぶんの transform 書き換えだけで行う（B-3 と同じ「毎回書くのは
  // 変位だけ」の規律。書き込み回数は従来の box-shadow 方式と同じ 1 ドラッグ 1 回/移動）。
  const shadowK = DRAG_LIFT_PX / (SHADOW_PANEL_Z - DRAG_LIFT_PX);
  let shadowCentroid = { x: 0, y: 0 };
  const shadowPaths: SVGPathElement[] = [];

  const beginShadow = (cells: readonly CellIndex[]): void => {
    shadowGroup.replaceChildren();
    shadowPaths.length = 0;
    if (cells.length === 0) return;
    let sx = 0;
    let sy = 0;
    for (const cell of cells) {
      sx += col(cell, C) * size.w + size.w / 2;
      sy += row(cell, C) * size.h + size.h / 2;
    }
    shadowCentroid = { x: sx / cells.length, y: sy / cells.length };
    const d = outlinePath(cells, C, size.w, size.h, 0);
    for (let i = 0; i < SHADOW_PANELS.length; i++) {
      const path = document.createElementNS(SVG_NS, 'path');
      path.setAttribute('class', 'drag-shadow-path');
      path.setAttribute('d', d);
      shadowGroup.append(path);
      shadowPaths.push(path);
    }
    moveShadow(0, 0);
  };

  const moveShadow = (dx: number, dy: number): void => {
    const px = shadowCentroid.x + dx;
    const py = shadowCentroid.y + dy;
    for (const [i, panel] of SHADOW_PANELS.entries()) {
      const path = shadowPaths[i];
      if (path === undefined) continue;
      // 04b §4.1: offset = (P − C_i) · h / (C_z − h)。
      const ox = dx + (px - panel.x) * shadowK;
      const oy = dy + (py - panel.y) * shadowK;
      path.setAttribute('transform', `translate(${ox.toFixed(2)} ${oy.toFixed(2)})`);
    }
  };

  const setCandidate = (cells: readonly CellIndex[]): void => {
    candidateGroup.replaceChildren();
    for (const cell of cells) {
      const rect = document.createElementNS(SVG_NS, 'rect');
      rect.setAttribute('class', 'drop-candidate');
      rect.setAttribute('x', String(col(cell, C) * size.w));
      rect.setAttribute('y', String(row(cell, C) * size.h));
      rect.setAttribute('width', String(size.w));
      rect.setAttribute('height', String(size.h));
      candidateGroup.append(rect);
    }
  };

  const setOutlines = (footprints: OutlineFootprints): void => {
    outlineGroup.replaceChildren();
    const append = (cells: readonly CellIndex[], variant: 'decomposed' | 'displaced'): void => {
      const path = document.createElementNS(SVG_NS, 'path');
      path.setAttribute('class', `preview preview--${variant}`);
      path.setAttribute('d', outlinePath(cells, C, size.w, size.h, PREVIEW_OFFSET));
      outlineGroup.append(path);
    };
    // 赤（分解）を白（維持）より手前に置く。警告が装飾に隠れてはならない。
    for (const cells of footprints.displaced) append(cells, 'displaced');
    for (const cells of footprints.decomposed) append(cells, 'decomposed');
  };

  return {
    setCandidate,
    setOutlines,
    beginShadow,
    moveShadow,
    clear: () => {
      shadowGroup.replaceChildren();
      shadowPaths.length = 0;
      candidateGroup.replaceChildren();
      outlineGroup.replaceChildren();
    },
  };
}
